// Shared tool helpers.

import * as state from '../state.js';

// Wires a freshly-created Konva node into the engine: makes it draggable and
// selectable, attaches the create-op to undo/redo, and emits a select on it
// once placed.
//
// CleanShot-style behavior: hovering over any annotation shows a grab cursor
// and clicking it selects/moves it — regardless of which drawing tool is
// active. `cancelBubble` keeps the active tool's draw handler from firing
// when the click lands on an existing node.
export function attachInteractive(engine, node, { deferOp = false, parent: parentLayer } = {}) {
  // Tag the node with the tool that created it, so the engine can route
  // live property changes (color, strokeWidth, fill, dashed, head, …) back
  // to the right tool's `apply()` when this node is selected.
  if (!node._kind) node._kind = state.get('activeTool');

  node.draggable(true);
  // Don't visually scale the stroke when the user resizes via the transformer
  // — they expect a "Thick" stroke to stay 10px regardless of how big the
  // shape gets. We pair this with `bakeScale` below so the node ends up in a
  // clean scaleX/Y=1 state after the resize, with its dimensions / points /
  // radii absorbing the scale.
  node.strokeScaleEnabled?.(false);
  node.on('transformend', () => bakeScale(node));
  expandHitArea(node);

  node.on('pointerdown', (e) => {
    e.cancelBubble = true;
    engine.selectNode(node);
  });

  node.on('pointerenter mouseenter', () => {
    engine.setCursor(node.isDragging() ? 'grabbing' : 'grab');
  });
  node.on('pointerleave mouseleave', () => {
    if (!node.isDragging()) engine.setCursor(null);
  });
  node.on('dragstart', () => {
    engine.setCursor('grabbing');
  });
  node.on('dragend', () => {
    // Cursor is still over the node we just released — go back to grab, not
    // the host's CSS cursor (which would briefly flash crosshair).
    engine.setCursor('grab');
  });

  // Most tools render to contentLayer; screenshot-modifying tools (blur)
  // pass `parent: engine.bgEffectsLayer` so their nodes sit beneath the
  // highlight scrim and get dimmed alongside the rest of the screenshot
  // when the user hasn't highlighted that region.
  const parent = parentLayer ?? engine.contentLayer;
  parent.add(node);

  // Tools that need to commit/cancel a node *after* placement (e.g. text
  // entering an inline edit before the user has typed anything) can defer
  // the undo op and call `commitInteractive(engine, node)` once the node
  // is real. If they cancel, they just destroy the node and no op is pushed.
  if (deferOp) return;
  engine.pushOp({
    undo: () => { node.remove(); engine.deselect(); },
    redo: () => { parent.add(node); },
  });
}

export function commitInteractive(engine, node, { parent: parentLayer } = {}) {
  const parent = parentLayer ?? engine.contentLayer;
  engine.pushOp({
    undo: () => { node.remove(); engine.deselect(); },
    redo: () => { parent.add(node); },
  });
}

export function pointerPos(engine) {
  return engine.stage.getPointerPosition() ?? { x: 0, y: 0 };
}

// Make the entire visual area of a node hover/click-targetable, even when its
// fill is transparent (unfilled rects / ellipses) or it is a thin line. Without
// this, Konva only registers hits on the stroke pixels — so hovering inside an
// outlined rectangle never fires `pointerenter` and the cursor never switches
// to "grab".
function expandHitArea(node) {
  const className = node.getClassName?.();

  if (className === 'Rect') {
    node.hitFunc(function (ctx, shape) {
      ctx.beginPath();
      ctx.rect(0, 0, shape.width(), shape.height());
      ctx.closePath();
      ctx.fillStrokeShape(shape);
    });
    return;
  }

  if (className === 'Ellipse') {
    node.hitFunc(function (ctx, shape) {
      ctx.beginPath();
      ctx.ellipse(0, 0, shape.radiusX(), shape.radiusY(), 0, 0, Math.PI * 2);
      ctx.closePath();
      ctx.fillStrokeShape(shape);
    });
    return;
  }

  // Lines, arrows, pencil strokes — give the user a comfortable click target
  // around the otherwise hairline stroke.
  if (className === 'Line' || className === 'Arrow') {
    const stroke = node.strokeWidth?.() ?? 4;
    node.hitStrokeWidth(Math.max(20, stroke + 12));
  }
}

// After a resize handle release, fold the transformer's scale back into the
// shape's intrinsic dimensions so the node is in a normalized state (scaleX/Y
// === 1). Pairs with `strokeScaleEnabled(false)` so the stroke is never
// visually distorted by intermediate scales.
function bakeScale(node) {
  const sx = node.scaleX?.() ?? 1;
  const sy = node.scaleY?.() ?? 1;
  if (sx === 1 && sy === 1) return;
  const className = node.getClassName?.();

  if (className === 'Rect') {
    node.setAttrs({
      width: Math.max(2, node.width() * sx),
      height: Math.max(2, node.height() * sy),
      scaleX: 1,
      scaleY: 1,
    });
  } else if (className === 'Ellipse') {
    node.setAttrs({
      radiusX: Math.max(2, node.radiusX() * sx),
      radiusY: Math.max(2, node.radiusY() * sy),
      scaleX: 1,
      scaleY: 1,
    });
  } else if (className === 'Line' || className === 'Arrow') {
    const points = node.points().map((v, i) => (i % 2 === 0 ? v * sx : v * sy));
    node.points(points);
    node.setAttrs({ scaleX: 1, scaleY: 1 });
  } else if (className === 'Image') {
    node.setAttrs({
      width: node.width() * sx,
      height: node.height() * sy,
      scaleX: 1,
      scaleY: 1,
    });
  }
  // Groups (text, number, bug-pin) intentionally not normalized here — their
  // children would need per-child handling, and a uniform Group scale produces
  // the correct visual today.
}
